"use client";

import { useState, useEffect, memo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { SharedNavbar } from '@/components/SharedNavbar';
import { GlobalToolsExpandingPanel } from '@/components/GlobalToolsExpandingPanel';
import { PageTransition } from '@/components/PageTransition';
import { ToolPreview } from '@/components/ToolPreview';
import { ProjectCard } from '@/components/ProjectCard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { PageFadeIn } from '@/components/PageFadeIn';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Grid3X3,
  Users,
  Shield,
  BookOpen,
  Calendar1,
  Calendar,
  CheckSquare,
  ArrowLeft,
  MoreHorizontal,
  MoreVertical,
  MessageCircle,
  FileText,
  MessageSquare,
  CheckCircle2,
  GripVertical,
  Trash2,
  Layers,
  ChevronRight,
  X,
  LayoutGrid
} from 'lucide-react';

// Tool configurations with icons and colors
const toolConfigs = {
  matrix: {
    name: "Matrix",
    icon: Grid3X3,
    description: "Training competency matrices",
    color: "bg-red-50 border-red-200",
    iconColor: "text-red-600",
    content: "Manage skills and certifications across your workforce"
  },
  team: {
    name: "Team",
    icon: Users,
    description: "Team member management",
    color: "bg-blue-50 border-blue-200",
    iconColor: "text-blue-600",
    content: "Track roles, responsibilities and progress"
  },
  compliance: {
    name: "AI Compliance",
    icon: Shield,
    description: "AI-powered compliance tracking",
    color: "bg-green-50 border-green-200",
    iconColor: "text-green-600",
    content: "Track regulatory requirements with AI assistance"
  },
  qualifications: {
    name: "Qualifications",
    icon: BookOpen,
    description: "Training requirements management",
    color: "bg-orange-50 border-orange-200",
    iconColor: "text-orange-600",
    content: "Manage qualifications and certifications needed"
  },
  bookings: {
    name: "Bookings",
    icon: Calendar1,
    description: "Training session scheduling",
    color: "bg-purple-50 border-purple-200",
    iconColor: "text-purple-600",
    content: "Schedule workshops and resource bookings"
  },
  calendar: {
    name: "Calendar",
    icon: Calendar,
    description: "Important dates and milestones",
    color: "bg-pink-50 border-pink-200",
    iconColor: "text-pink-600",
    content: "Set deadlines and sync with external calendars"
  },
  tasks: {
    name: "Tasks",
    icon: CheckSquare,
    description: "Task management and tracking",
    color: "bg-emerald-50 border-emerald-200",
    iconColor: "text-emerald-600",
    content: "Manage and track tasks across your team"
  },
  // Legacy alias - maps to tasks
  checklists: {
    name: "Tasks",
    icon: CheckSquare,
    description: "Task management and tracking",
    color: "bg-emerald-50 border-emerald-200",
    iconColor: "text-emerald-600",
    content: "Manage and track tasks across your team"
  },
  chat: {
    name: "Chat",
    icon: MessageCircle,
    description: "Real-time team communication",
    color: "bg-indigo-50 border-indigo-200",
    iconColor: "text-indigo-600",
    content: "Direct messages, group chats, and file sharing"
  },
  docs: {
    name: "Docs & Files",
    icon: FileText,
    description: "Document management",
    color: "bg-amber-50 border-amber-200",
    iconColor: "text-amber-600",
    content: "Store and collaborate on documents and training materials"
  },
  message_board: {
    name: "Posts",
    icon: MessageSquare,
    description: "Team announcements",
    color: "bg-cyan-50 border-cyan-200",
    iconColor: "text-cyan-600",
    content: "Post updates and discussions with threaded conversations"
  },
  checkins: {
    name: "Check-ins",
    icon: CheckCircle2,
    description: "Team status updates",
    color: "bg-teal-50 border-teal-200",
    iconColor: "text-teal-600",
    content: "Regular check-ins to track progress and team sentiment"
  }
};

export const StackLandingPage = () => {
  const { stackId } = useParams();
  const navigate = useNavigate();
  const { userProfile, company, user } = useAuth();
  const [globalToolsOpen, setGlobalToolsOpen] = useState(false);
  const handleGlobalToolsToggle = useCallback(() => setGlobalToolsOpen(prev => !prev), []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stack, setStack] = useState(null);
  const [projects, setProjects] = useState([]);
  const [toolOrder, setToolOrder] = useState([]);
  const [allTeamMembers, setAllTeamMembers] = useState([]);
  const [removingProjectId, setRemovingProjectId] = useState(null);
  const [projectToRemove, setProjectToRemove] = useState(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  usePageTitle(stack?.name ? `${stack.name}` : 'Stack');

  useEffect(() => {
    const loadStackData = async () => {
      if (!company?.id || !userProfile?.id) {
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Load stack data from user_dashboard_settings
        const { data: settingsData, error: settingsError } = await supabase
          .from('user_dashboard_settings')
          .select('stacks')
          .eq('user_id', userProfile.id)
          .eq('company_id', company.id)
          .single();

        if (settingsError && settingsError.code !== 'PGRST116') {
          throw settingsError;
        }

        // Find the specific stack
        const stackData = settingsData?.stacks?.find(s => s.id.toString() === stackId.toString());

        if (!stackData) {
          throw new Error('Stack not found');
        }

        setStack(stackData);

        // Load all projects in the stack
        if (stackData.projectIds && stackData.projectIds.length > 0) {
          const { data: projectsData, error: projectsError } = await supabase
            .from('projects')
            .select('*')
            .in('id', stackData.projectIds)
            .eq('company_id', company.id)
            .eq('is_archived', false);

          if (projectsError) throw projectsError;

          const formattedProjects = projectsData || [];

          // Load team members and compliance summaries for each project separately
          const projectsWithMembers = await Promise.all(
            formattedProjects.map(async (project) => {
              // Fetch team members and compliance summary in parallel
              const [membersResult, complianceResult] = await Promise.all([
                supabase
                  .from('space_members')
                  .select(`
                    user_id,
                    role,
                    user_profiles!user_id (
                      id,
                      email,
                      first_name,
                      last_name,
                      avatar_url
                    )
                  `)
                  .eq('project_id', project.id),
                supabase.rpc('calculate_project_compliance_summary', { p_project_id: project.id })
              ]);

              const teamMembers = membersResult.data?.map(member => ({
                id: member.user_profiles.id,
                name: `${member.user_profiles.first_name || ''} ${member.user_profiles.last_name || ''}`.trim() || member.user_profiles.email,
                email: member.user_profiles.email,
                avatar: member.user_profiles.avatar_url || null,
                role: member.role,
              })) || [];

              // Extract compliance summary from RPC result
              const complianceSummary = complianceResult.data || null;

              return {
                ...project,
                teamMembers,
                complianceSummary
              };
            })
          );

          setProjects(projectsWithMembers);

          console.log('[StackLandingPage] Loaded projects:', projectsWithMembers.map(p => ({ id: p.id, name: p.name })));

          // In stack view, only show tools enabled in ALL spaces (intersection)
          // This prevents gaps from tools that aren't shared across all spaces
          let sharedTools = null;
          projectsWithMembers.forEach(project => {
            // Handle both JSON string and object (JSONB from database)
            let tools = {};
            if (project.tools) {
              if (typeof project.tools === 'string') {
                try {
                  tools = JSON.parse(project.tools);
                } catch (e) {
                  console.error('Error parsing tools JSON:', e, project.tools);
                }
              } else if (typeof project.tools === 'object') {
                tools = project.tools;
              }
            }
            // Get enabled tools for this project (excluding chat)
            const projectTools = new Set(
              Object.entries(tools)
                .filter(([key, enabled]) => enabled && key !== 'chat')
                .map(([key]) => key)
            );

            // First project sets the initial set, subsequent projects intersect
            if (sharedTools === null) {
              sharedTools = projectTools;
            } else {
              // Keep only tools that exist in both sets (intersection)
              sharedTools = new Set([...sharedTools].filter(tool => projectTools.has(tool)));
            }
          });

          // Convert to array and set as tool order
          const allTools = sharedTools ? Array.from(sharedTools) : [];
          setToolOrder(allTools);
          console.log('[StackLandingPage] Showing shared tools across all spaces (excluding chat):', allTools);

          // Collect all unique team members from all projects for the main display
          const memberMap = new Map();
          projectsWithMembers.forEach(project => {
            project.teamMembers?.forEach(member => {
              if (!memberMap.has(member.id)) {
                memberMap.set(member.id, {
                  ...member,
                  initials: `${(member.name.split(' ')[0]?.[0] || member.email[0])}${(member.name.split(' ')[1]?.[0] || '')}`.toUpperCase()
                });
              }
            });
          });

          setAllTeamMembers(Array.from(memberMap.values()));
        } else {
          setProjects([]);
        }
      } catch (err) {
        console.error('Error loading stack data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadStackData();
  }, [stackId, userProfile?.id, company?.id]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px of movement before drag activates
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = toolOrder.indexOf(active.id);
      const newIndex = toolOrder.indexOf(over.id);
      const newOrder = arrayMove(toolOrder, oldIndex, newIndex);
      setToolOrder(newOrder);
    }
  };

  // Show confirmation dialog
  const handleRemoveClick = (project) => {
    setProjectToRemove(project);
    setShowRemoveConfirm(true);
  };

  // Handle removing a space from the stack with animation
  const handleConfirmRemove = async () => {
    if (!userProfile?.id || !company?.id || !projectToRemove) return;

    try {
      // Start animation
      setRemovingProjectId(projectToRemove.id);
      setShowRemoveConfirm(false);

      // Wait for animation to complete
      await new Promise(resolve => setTimeout(resolve, 800));

      // Remove project from local state
      const updatedProjects = projects.filter(p => p.id !== projectToRemove.id);
      setProjects(updatedProjects);

      // Update the stack in Supabase
      const { data: settingsData } = await supabase
        .from('user_dashboard_settings')
        .select('stacks')
        .eq('user_id', userProfile.id)
        .eq('company_id', company.id)
        .single();

      if (settingsData?.stacks) {
        const updatedStacks = settingsData.stacks.map(s => {
          if (s.id.toString() === stackId.toString()) {
            return {
              ...s,
              projectIds: s.projectIds.filter(id => id !== projectToRemove.id)
            };
          }
          return s;
        });

        await supabase
          .from('user_dashboard_settings')
          .update({ stacks: updatedStacks })
          .eq('user_id', userProfile.id)
          .eq('company_id', company.id)
            .eq('is_archived', false);
      }

      setRemovingProjectId(null);
      setProjectToRemove(null);
    } catch (error) {
      console.error('Error removing space from stack:', error);
      setRemovingProjectId(null);
      setProjectToRemove(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !stack) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error: {error || 'Stack not found'}</p>
          <Button onClick={() => navigate('/dashboard')} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const SortableToolCard = memo(({ toolKey, config, projectId, projectIds }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: toolKey });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      zIndex: isDragging ? 1000 : 'auto'
    };

    return (
      <div ref={setNodeRef} style={style} className="w-full h-full">
        <ToolCard
          toolKey={toolKey}
          config={config}
          projectId={projectId}
          projectIds={projectIds}
          dragListeners={listeners}
          dragAttributes={attributes}
        />
      </div>
    );
  });

  const ToolCard = memo(({ toolKey, config, projectId, projectIds, dragListeners, dragAttributes }) => {
    if (!config) {
      console.warn(`No configuration found for tool: ${toolKey}`);
      return null;
    }

    const IconComponent = config.icon;

    const handleClick = () => {
      // For stacks with multiple projects, navigate to stack tool pages
      if (projectIds && projectIds.length > 1) {
        if (toolKey === 'matrix') {
          navigate(`/stack/${stackId}/matrix`);
        } else if (toolKey === 'team') {
          navigate(`/stack/${stackId}/team`);
        } else if (toolKey === 'qualifications') {
          navigate(`/stack/${stackId}/qualifications`);
        } else if (toolKey === 'bookings') {
          navigate(`/stack/${stackId}/bookings`);
        } else if (toolKey === 'calendar') {
          navigate(`/stack/${stackId}/schedule`);
        } else if (toolKey === 'tasks' || toolKey === 'checklists') {
          navigate(`/stack/${stackId}/tasks`);
        } else if (toolKey === 'docs') {
          navigate(`/stack/${stackId}/docs`);
        } else if (toolKey === 'message_board') {
          navigate(`/stack/${stackId}/message-board`);
        } else if (toolKey === 'checkins') {
          navigate(`/stack/${stackId}/checkins`);
        } else {
          // Other tools not yet implemented
          console.log(`Stack ${toolKey} page not yet implemented. Viewing aggregated preview only.`);
        }
        return;
      }

      // For single project, navigate to project tool view
      if (projectId) {
        if (toolKey === 'matrix') {
          navigate(`/space/${projectId}/matrix`);
        } else if (toolKey === 'team') {
          navigate(`/space/${projectId}/team`);
        } else if (toolKey === 'compliance') {
          navigate(`/space/${projectId}/compliance`);
        } else if (toolKey === 'qualifications') {
          navigate(`/space/${projectId}/qualifications`);
        } else if (toolKey === 'bookings') {
          navigate(`/space/${projectId}/bookings`);
        } else if (toolKey === 'calendar') {
          navigate(`/space/${projectId}/schedule`);
        } else if (toolKey === 'tasks' || toolKey === 'checklists') {
          navigate(`/space/${projectId}/tasks`);
        } else if (toolKey === 'chat') {
          navigate(`/chat?space=${projectId}`);
        } else if (toolKey === 'docs') {
          navigate(`/space/${projectId}/docs`);
        } else if (toolKey === 'message_board') {
          navigate(`/space/${projectId}/message-board`);
        } else if (toolKey === 'checkins') {
          navigate(`/space/${projectId}/checkins`);
        }
      }
    };

    // Determine if this card should be clickable
    // Matrix, Team, Qualifications, Bookings, Calendar, Tasks, Docs, Posts, and Check-ins are clickable for stacks, others are clickable for single projects
    const isClickable = (projectIds && projectIds.length > 1 && (toolKey === 'matrix' || toolKey === 'team' || toolKey === 'qualifications' || toolKey === 'bookings' || toolKey === 'calendar' || toolKey === 'tasks' || toolKey === 'checklists' || toolKey === 'docs' || toolKey === 'message_board' || toolKey === 'checkins')) ||
                        (projectIds && projectIds.length === 1) ||
                        projectId;

    return (
      <Card
        className={`group hover:shadow-xl transition-all duration-300 h-full flex flex-col border-0 overflow-hidden relative ${isClickable ? 'cursor-pointer hover:scale-[1.02]' : 'cursor-default'}`}
        onClick={isClickable ? handleClick : undefined}
        {...dragListeners}
        {...dragAttributes}
      >
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2 flex-1 overflow-hidden">
              <div className={`p-1.5 ${config.color} rounded-lg flex-shrink-0`}>
                <IconComponent className={`h-4 w-4 ${config.iconColor}`} />
              </div>
              <div className="flex-1 overflow-hidden">
                <CardTitle className="text-base font-semibold leading-tight">
                  {config.name}
                </CardTitle>
                <CardDescription className="text-xs mt-1 whitespace-nowrap overflow-hidden text-ellipsis">
                  {config.description}
                </CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex-1 flex flex-col pt-0">
          <div className="flex-1 border-t pt-3">
            {projectIds && projectIds.length > 0 ? (
              <ToolPreview toolKey={toolKey} projectIds={projectIds} stackId={stackId} />
            ) : projectId ? (
              <ToolPreview toolKey={toolKey} projectId={projectId} />
            ) : (
              <div className="flex items-center justify-center h-full text-xs text-muted-foreground">
                No preview available
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  });

  const firstProject = projects[0];
  const enabledTools = toolOrder;

  // Handler for removing project from stack (used in ProjectCard dropdown)
  const handleProjectColorChange = (projectId, color) => {
    // Update project color in local state
    setProjects(prevProjects =>
      prevProjects.map(p =>
        p.id === projectId ? { ...p, color } : p
      )
    );
  };

  // If no projects, show empty state
  if (!firstProject && !loading) {
    return (
      <PageTransition>
        <div className="bg-muted/40 min-h-screen flex flex-col">
          <SharedNavbar isGlobalToolsOpen={globalToolsOpen} onGlobalToolsToggle={handleGlobalToolsToggle} />
          <GlobalToolsExpandingPanel isOpen={globalToolsOpen} onClose={() => setGlobalToolsOpen(false)} currentTool={null} />

          <div className="flex-1 overflow-y-auto p-6">
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">This stack is empty</p>
              <Button onClick={() => navigate('/dashboard')} variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className="bg-muted/40 min-h-screen flex flex-col">
        <SharedNavbar />

        {/* Main content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-6xl mx-auto space-y-8 p-6">
          {/* Stack Avatar and Title */}
          <div className="text-center space-y-6">
            <div className="flex flex-col items-center space-y-4">
              <div className="h-16 w-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center shadow-lg">
                <Layers className="h-8 w-8 text-white" />
              </div>
              <div className="relative inline-block">
                <h1 className="text-3xl font-bold">{stack.name}</h1>
              </div>
            </div>

            {/* Team Members - All unique members from all spaces */}
            <div className="flex flex-col items-center space-y-3">
              {allTeamMembers.length > 0 && (
                <>
                  <TooltipProvider delayDuration={0}>
                    <div className="flex flex-row items-center -space-x-2">
                      {allTeamMembers.slice(0, 5).map((member, index) => (
                        <Tooltip key={member.id}>
                          <TooltipTrigger asChild>
                            <div
                              className="relative transition-transform hover:-translate-y-1 cursor-pointer"
                              style={{ zIndex: index }}
                            >
                              <Avatar className="h-10 w-10 border-2 border-white">
                                <AvatarImage
                                  src={member.avatar}
                                  alt={member.name}
                                  className="object-cover"
                                />
                                <AvatarFallback>{member.initials}</AvatarFallback>
                              </Avatar>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="top" sideOffset={10}>
                            <div className="space-y-1">
                              <p className="font-semibold">{member.name}</p>
                              <p className="text-xs text-muted-foreground">{member.email}</p>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      ))}
                      {allTeamMembers.length > 5 && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div
                              className="relative transition-transform hover:-translate-y-1 cursor-pointer"
                              style={{ zIndex: 5 }}
                            >
                              <Avatar className="h-10 w-10 border-2 border-white bg-gray-200">
                                <AvatarFallback className="text-sm font-semibold">
                                  +{allTeamMembers.length - 5}
                                </AvatarFallback>
                              </Avatar>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="top" sideOffset={10}>
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                              <p className="text-xs text-muted-foreground font-semibold mb-2">
                                {allTeamMembers.length - 5} more member{allTeamMembers.length - 5 !== 1 ? 's' : ''}:
                              </p>
                              {allTeamMembers.slice(5).map((member) => (
                                <div key={member.id} className="space-y-1 pb-2">
                                  <p className="font-semibold">{member.name}</p>
                                  <p className="text-xs text-muted-foreground">{member.email}</p>
                                </div>
                              ))}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </TooltipProvider>
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <Users className="h-4 w-4" />
                    <span>
                      {allTeamMembers.length} team member{allTeamMembers.length !== 1 ? 's' : ''} across all spaces
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Spaces in this Stack - Grid Layout */}
          <div className="space-y-6">
            <div className="flex items-center justify-center">
              <div className="flex items-center gap-2">
                <Layers className="h-5 w-5 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Spaces in this Stack
                </h3>
                <Badge variant="secondary" className="ml-2">
                  {projects.length}
                </Badge>
              </div>
            </div>

            {/* Grid layout for spaces - centered */}
            <AnimatePresence mode="popLayout">
              <div className="flex justify-center">
                <div className="flex flex-wrap justify-center gap-4 w-full max-w-7xl">
                  {projects.map((project) => {
                    // Parse tools to count enabled ones
                    let tools = {};
                    if (project.tools) {
                      if (typeof project.tools === 'string') {
                        try {
                          tools = JSON.parse(project.tools);
                        } catch (e) {
                          console.error('Error parsing tools JSON:', e);
                        }
                      } else if (typeof project.tools === 'object') {
                        tools = project.tools;
                      }
                    }
                    const enabledToolsCount = Object.values(tools).filter(Boolean).length;

                    // Add tool count to project for ProjectCard
                    const projectWithToolCount = {
                      ...project,
                      toolCount: enabledToolsCount
                    };

                    return (
                      <motion.div
                        key={project.id}
                        layout
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{
                          opacity: 0,
                          scale: 0.9,
                          transition: { duration: 0.2 }
                        }}
                        transition={{ duration: 0.3 }}
                        className="w-[calc(50%-8px)] sm:w-[300px] flex"
                      >
                        <ProjectCard
                          project={projectWithToolCount}
                          onColorChange={handleProjectColorChange}
                          onPin={null}
                          isDragActive={false}
                          isDropTarget={false}
                          stackId={stackId}
                          stackName={stack.name}
                          extraMenuItems={
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveClick(project);
                              }}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Remove from Stack
                            </DropdownMenuItem>
                          }
                        />
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </AnimatePresence>
          </div>

          {/* Remove Confirmation Dialog */}
          <Dialog open={showRemoveConfirm} onOpenChange={setShowRemoveConfirm}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Remove Space from Stack?</DialogTitle>
                <DialogDescription>
                  Are you sure you want to remove "{projectToRemove?.name}" from this stack? The space will still be available on your dashboard.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowRemoveConfirm(false)}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={handleConfirmRemove}>
                  Remove
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Tools Section */}
          <div className="space-y-6">
            <div className="flex items-center justify-center">
              <div className="flex items-center gap-2">
                <LayoutGrid className="h-5 w-5 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Tools
                </h3>
                <Badge variant="secondary" className="ml-2">
                  {enabledTools.length}
                </Badge>
              </div>
            </div>

            {/* Tools Grid */}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={enabledTools}
                strategy={rectSortingStrategy}
              >
                <div className="flex justify-center">
                  <div className="flex flex-wrap justify-center gap-4 w-full max-w-7xl">
                    {enabledTools.map((toolKey) => {
                    const projectIdArray = projects.map(p => p.id);
                    console.log('[StackLandingPage] Rendering tool:', toolKey, 'with projectIds:', projectIdArray);
                    return (
                      <div key={toolKey} className="w-[calc(50%-8px)] sm:w-[300px] flex">
                        <SortableToolCard
                          toolKey={toolKey}
                          config={toolConfigs[toolKey]}
                          projectId={firstProject?.id}
                          projectIds={projectIdArray}
                        />
                      </div>
                      );
                    })}
                  </div>
                </div>
              </SortableContext>
            </DndContext>
          </div>

          </div>
        </div>
      </div>
    </PageTransition>
  );
};
